import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { login as apiLogin, getMe } from '../api/auth';
import type { LoginDto, JwtUser } from '../types';

const TOKEN_KEY = 'mgbotisp_token';

function getStoredToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function removeStoredTokens(): void {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    // Tokens issued by older builds must not keep a browser authenticated.
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Storage can be unavailable in private/restricted browser contexts.
  }
}

function storeToken(token: string): void {
  sessionStorage.setItem(TOKEN_KEY, token);
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Ignore unavailable localStorage; sessionStorage still holds the token.
  }
}

interface AuthContextValue {
  user: JwtUser | null;
  loading: boolean;
  error: string | null;
  login: (dto: LoginDto) => Promise<void>;
  logout: () => void;
  token: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function decodeToken(token: string): JwtUser | null {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded as JwtUser;
  } catch {
    return null;
  }
}

function normalizeUser(user: JwtUser, token: string): JwtUser {
  const decoded = decodeToken(token);
  return {
    ...decoded,
    ...user,
    sub: user.sub || user.id || decoded?.sub || '',
    name: user.name || decoded?.name || user.email,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<JwtUser | null>(() => {
    // Migrate away from the previous persistent localStorage session.
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      // Ignore unavailable localStorage.
    }
    const stored = getStoredToken();
    if (stored) {
      const decoded = decodeToken(stored);
      if (decoded && decoded.exp * 1000 > Date.now()) {
        return decoded;
      }
      removeStoredTokens();
    }
    return null;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const token = user ? getStoredToken() : null;

  const login = useCallback(async (dto: LoginDto) => {
    setError(null);
    setLoading(true);
    try {
      const response = await apiLogin(dto);
      storeToken(response.accessToken);
      const decoded = decodeToken(response.accessToken);
      setUser(response.user ? normalizeUser(response.user, response.accessToken) : decoded);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Falha ao conectar';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    removeStoredTokens();
    setUser(null);
    setError(null);
  }, []);

  useEffect(() => {
    const stored = getStoredToken();
    if (stored) {
      const decoded = decodeToken(stored);
      if (decoded && decoded.exp * 1000 > Date.now()) {
        getMe()
          .then((currentUser) => setUser(normalizeUser(currentUser, stored)))
          .catch(() => {
            removeStoredTokens();
            setUser(null);
          })
          .finally(() => setLoading(false));
      } else {
        removeStoredTokens();
        setUser(null);
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout, token }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
